import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import type { TicketLookupFilter } from '@/gql/graphql';
import { FilterBar } from '@/components/ui/FilterBar';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { formatDateTime } from '@/lib/format';
import { Can } from '@/lib/permissions';
import { useDebouncedUrlField, useUrlState } from '@/hooks/useUrlState';
import { AdminTicketsGrid } from './AdminTicketsGrid';
import {
  NightsForTicketsFilterDoc,
  TICKET_KIND_FILTER_OPTIONS,
  TICKET_SALE_CHANNEL_FILTER_OPTIONS,
  TICKET_STATUS_FILTER_OPTIONS,
  TicketsDoc,
} from './adminTickets';
import { ticketsUrlSchema } from './ticketsUrlState';

export function TicketsListPage() {
  const { state: url, patch, setFilters } = useUrlState(ticketsUrlSchema);
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));

  const filter = useMemo<TicketLookupFilter>(
    () => ({
      search: url.search.trim() || null,
      status: url.status ? [url.status] : null,
      kind: url.kind ? [url.kind] : null,
      saleChannel: url.channel ? [url.channel] : null,
      nightId: url.night || null,
      unclaimed: url.unclaimed || null,
    }),
    [url.search, url.status, url.kind, url.channel, url.night, url.unclaimed],
  );

  const { data, previousData, loading, error, refetch } = useQuery(TicketsDoc, {
    variables: { filter, page: url.page, pageSize: url.size },
    errorPolicy: 'all',
  });
  const nightsQuery = useQuery(NightsForTicketsFilterDoc, {
    errorPolicy: 'all',
  });
  const viewData = data ?? previousData;
  const rows = viewData?.tickets.nodes ?? [];
  const pageInfo = viewData?.tickets.pageInfo;
  const nights = nightsQuery.data?.nights.nodes ?? [];

    return (
    <div className="space-y-4" id="admin.tickets.list">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Tickets
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Every ticket in this venue, newest first. Filter by status, type, or
          night, or search the buyer or claimer e-mail, night, phase, or an
          order / ticket id.
        </p>
      </div>

      {error ? (
        <QueryErrorNotice error={error} title="Could not load tickets" />
      ) : null}

      <FilterBar
        search={{
          id: 'admin.tickets.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Buyer or claimer e-mail, night, phase, or UUID',
        }}
        primary={
          <div className="flex flex-wrap gap-2">
            <Select
              id="admin.tickets.status"
              value={url.status}
              onChange={(e) => setFilters({ status: e.target.value as typeof url.status })}
              options={TICKET_STATUS_FILTER_OPTIONS}
            />
            <Select
              id="admin.tickets.kind"
              value={url.kind}
              onChange={(e) => setFilters({ kind: e.target.value as typeof url.kind })}
              options={TICKET_KIND_FILTER_OPTIONS}
            />
            <div className="flex h-10 items-center">
              <Switch
                id="admin.tickets.unclaimed"
                label="Unclaimed only"
                checked={url.unclaimed}
                onChange={(e) => setFilters({ unclaimed: e.target.checked })}
              />
            </div>
            <Select
              id="admin.tickets.channel"
              value={url.channel}
              onChange={(e) => setFilters({ channel: e.target.value as typeof url.channel })}
              options={TICKET_SALE_CHANNEL_FILTER_OPTIONS}
            />
            <Can permission="night:read">
              <Select
                id="admin.tickets.night"
                value={url.night}
                onChange={(e) => setFilters({ night: e.target.value })}
                options={[
                  { value: '', label: 'All nights' },
                  ...nights.map((p) => ({
                    value: p.id,
                    label: `${p.title} · ${formatDateTime(p.startsAt)}`,
                  })),
                ]}
              />
            </Can>
          </div>
        }
      />

      <AdminTicketsGrid
        rows={rows}
        loading={loading && !viewData}
        page={pageInfo?.page ?? url.page}
        pageSize={pageInfo?.pageSize ?? url.size}
        totalRows={pageInfo?.totalRows ?? 0}
        totalPages={pageInfo?.totalPages ?? 1}
        onPageChange={(next) => patch({ page: next })}
        onPageSizeChange={(next) => setFilters({ size: next })}
        onChanged={() => refetch()}
        showNight
      />
    </div>
  );
}
